import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import ReactQuill from "react-quill";
import Select from "react-select";
import { Bounce, toast } from "react-toastify";
import "react-quill/dist/quill.snow.css";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const TEST_EMAIL = "webanalytis@gmail.com";
const SENDER_EMAIL = "aida@crea.dz";
const COPY_EMAIL = "idenetoran@gmail.com";

const DEFAULT_EMAIL_HTML = `
  <p>Bonjour,</p>
  <p>Votre espace personnel est desormais disponible sur la plateforme AIDA.</p>
  <p>Vous pouvez vous connecter afin de consulter votre profil, completer vos informations et suivre les services mis a votre disposition.</p>
  <p><strong>Vos informations d'acces :</strong></p>
  <ul>
    <li><strong>Email de connexion :</strong> {{email}}</li>
    <li><strong>Nom d'utilisateur :</strong> {{username}}</li>
    <li><strong>Lien de connexion :</strong> <a href="{{loginUrl}}">{{loginUrl}}</a></li>
  </ul>
  <p>Si vous souhaitez definir ou modifier votre mot de passe, veuillez utiliser le lien suivant :</p>
  <p><a href="{{forgotPasswordUrl}}"><strong>Changer mon mot de passe AIDA</strong></a></p>
  <p>Ce lien vous permettra d'acceder a la procedure securisee de changement du mot de passe.</p>
`;

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "align",
  "link",
];

const htmlToText = (html: string) => {
  const root = document.createElement("div");
  root.innerHTML = html;
  return root.textContent?.replace(/\n{3,}/g, "\n\n").trim() || "";
};

interface UserOption {
  value: number;
  label: string;
  email: string;
  role: string;
}

interface DispatchResult {
  id_user?: number;
  email: string;
  username: string;
  dispatchReference?: string;
  sent: boolean;
  test?: boolean;
  error?: string;
}

type DispatchMode = "test" | "selected" | "all_clients";

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: "46px",
    borderColor: "#d1d5db",
    borderRadius: "8px",
    boxShadow: "none",
    "&:hover": { borderColor: "#9ca3af" },
  }),
  menu: (base: any) => ({ ...base, zIndex: 20 }),
};

const EmailDispatch: React.FC = () => {
  const [mode, setMode] = useState<DispatchMode>("test");
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [subject, setSubject] = useState("Acces a votre espace AIDA");
  const [htmlMessage, setHtmlMessage] = useState(DEFAULT_EMAIL_HTML);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<DispatchResult[]>([]);
  const [summary, setSummary] = useState("");
  const [summaryVariant, setSummaryVariant] = useState<"success" | "warning" | "danger">("success");

  const canSend = useMemo(() => {
    if (sending) return false;
    if (mode === "selected") return selectedUsers.length > 0;
    return htmlToText(htmlMessage).length > 0;
  }, [htmlMessage, mode, selectedUsers.length, sending]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await fetch(
          `${backendUrl}/api/users?page=1&limit=500&sortColumn=id_user&sortOrder=ASC&search=&type=1`,
          { credentials: "include" },
        );
        const data = await response.json();

        const options: UserOption[] = (Array.isArray(data) ? data : [])
          .filter((user) => user.id_user !== 1 && user.email_user)
          .map((user) => ({
            value: user.id_user,
            label: `#${user.id_user} - ${user.username_user} (${user.email_user})`,
            email: user.email_user,
            role: user.role_name || "",
          }));

        setUsers(options);
      } catch (error) {
        console.error("Error loading users for email dispatch:", error);
        toast.error("Impossible de charger les utilisateurs", {
          position: "bottom-right",
          autoClose: 2400,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, []);

  const sendDispatch = async () => {
    if (!canSend) return;

    try {
      setSending(true);
      setResults([]);
      setSummary("");

      const requestBody = new FormData();
      requestBody.append("mode", mode);
      requestBody.append("userIds", JSON.stringify(selectedUsers.map((user) => user.value)));
      requestBody.append("testEmail", TEST_EMAIL);
      requestBody.append("subject", subject);
      requestBody.append("message", htmlToText(htmlMessage));
      requestBody.append("htmlMessage", htmlMessage);
      if (attachment) requestBody.append("attachment", attachment);

      const response = await fetch(`${backendUrl}/api/email-dispatch`, {
        method: "POST",
        credentials: "include",
        body: requestBody,
      });

      const data = await response.json();
      const failedCount = Number(data.failed || 0);

      if (!response.ok) {
        toast.error(data.message || "Erreur pendant l'envoi", {
          position: "bottom-right",
          autoClose: 3200,
          theme: "light",
          transition: Bounce,
        });
        setSummaryVariant("danger");
      } else if (failedCount > 0) {
        toast.warning(`${data.sent || 0} email(s) envoyé(s), ${failedCount} problème(s) à vérifier`, {
          position: "bottom-right",
          autoClose: 5000,
          theme: "light",
          transition: Bounce,
        });
        setSummaryVariant("warning");
      } else {
        toast.success("Envoi termine", {
          position: "bottom-right",
          autoClose: 2400,
          theme: "light",
          transition: Bounce,
        });
        setSummaryVariant("success");
      }

      setResults(Array.isArray(data.results) ? data.results : []);
      setSummary(
        mode === "test"
          ? `Test envoye vers ${TEST_EMAIL}, copie a ${COPY_EMAIL}`
          : `${data.sent || 0}/${data.requested || 0} email(s) envoye(s), ${failedCount} probleme(s), copie a ${COPY_EMAIL}`,
      );
    } catch (error) {
      console.error("Error sending email dispatch:", error);
      toast.error("Erreur serveur pendant l'envoi", {
        position: "bottom-right",
        autoClose: 3200,
        theme: "light",
        transition: Bounce,
      });
      setSummaryVariant("danger");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-fluid py-4 px-4 email-dispatch-page">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-1">
            <i className="las la-envelope-open-text mr-2"></i>
            Dispatch emails
          </h4>
          <div className="text-muted">
            Envoi de la communication AIDA depuis {SENDER_EMAIL}. Copie a{" "}
            {COPY_EMAIL}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-7 mb-4">
          <div className="email-dispatch-panel">
            <Form.Group className="mb-4">
              <Form.Label className="font-weight-bold">Mode d'envoi</Form.Label>
              <div className="email-mode-group">
                <button
                  type="button"
                  className={mode === "test" ? "active" : ""}
                  onClick={() => setMode("test")}
                >
                  <i className="las la-vial"></i>
                  Test
                </button>
                <button
                  type="button"
                  className={mode === "selected" ? "active" : ""}
                  onClick={() => setMode("selected")}
                >
                  <i className="las la-user-check"></i>
                  Selection
                </button>
                <button
                  type="button"
                  className={mode === "all_clients" ? "active" : ""}
                  onClick={() => setMode("all_clients")}
                >
                  <i className="las la-users"></i>
                  Tous clients
                </button>
              </div>
            </Form.Group>

            {mode === "test" && (
              <Alert variant="warning" className="email-dispatch-alert">
                Le test envoie un exemple vers {TEST_EMAIL}. Une copie sera
                envoyee a {COPY_EMAIL}. Aucun mot de passe client n'est modifie.
              </Alert>
            )}

            {mode === "selected" && (
              <Form.Group className="mb-4">
                <Form.Label className="font-weight-bold">
                  Clients a traiter
                </Form.Label>
                <Select
                  isMulti
                  isLoading={loadingUsers}
                  options={users}
                  value={selectedUsers}
                  onChange={(options) =>
                    setSelectedUsers([...(options as UserOption[])])
                  }
                  styles={selectStyles}
                  placeholder="Selectionner un ou plusieurs clients"
                />
              </Form.Group>
            )}

            {mode === "all_clients" && (
              <Alert variant="danger" className="email-dispatch-alert">
                Cet envoi diffuse le message a tous les fournisseurs et OEM avec
                email valide. Une copie sera envoyee a {COPY_EMAIL}.
              </Alert>
            )}

            <Form.Group className="mb-4">
              <Form.Label className="font-weight-bold">Sujet</Form.Label>
              <Form.Control
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="font-weight-bold">
                Editeur HTML
              </Form.Label>
              <ReactQuill
                theme="snow"
                value={htmlMessage}
                onChange={setHtmlMessage}
                modules={quillModules}
                formats={quillFormats}
                style={{ background: "#fff", minHeight: 360 }}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="font-weight-bold">Pièce jointe</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
                onChange={(event) => setAttachment((event.target as HTMLInputElement).files?.[0] || null)}
              />
              <Form.Text className="text-muted">
                Une pièce jointe par campagne, jusqu’à 10 Mo : PDF, Word, Excel, image ou ZIP.
              </Form.Text>
              {attachment && (
                <div className="small text-success mt-2">
                  Fichier sélectionné : {attachment.name}
                </div>
              )}
            </Form.Group>

            <Button
              type="button"
              className="email-dispatch-send"
              onClick={sendDispatch}
              disabled={!canSend}
            >
              <i className="las la-paper-plane"></i>
              {sending
                ? "Envoi..."
                : mode === "test"
                  ? "Envoyer le test"
                  : "Envoyer le message"}
            </Button>
          </div>
        </div>

        <div className="col-lg-5 mb-4">
          <div className="email-preview">
            <div className="email-preview-header">
              <div className="email-preview-brand-row">
                <img
                  src="/asset/images/cre-logo.png"
                  alt="CREA - Conseil du Renouveau Economique Algerien"
                  className="email-preview-logo"
                />
                <div className="email-preview-aida">
                  <strong>
                    <span>AI</span>DA
                  </strong>
                  <small>{SENDER_EMAIL}</small>
                </div>
              </div>
            </div>
            <div className="email-preview-body">
              <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />
              <small>Cordialement, l'equipe AIDA</small>
            </div>
          </div>
        </div>
      </div>

      {summary && (
        <div className="email-dispatch-panel">
          <Alert variant={summaryVariant} className="mb-3">
            {summary}
          </Alert>
          <div className="table-responsive">
            <Table className="table align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr key={`${result.email}-${index}`}>
                    <td>{result.id_user || "-"}</td>
                    <td>{result.email}</td>
                    <td>{result.username}</td>
                    <td>
                      <span
                        className={
                          result.sent
                            ? "email-status email-status-ok"
                            : "email-status email-status-error"
                        }
                      >
                        {result.sent
                          ? "Envoye"
                          : `Non envoye${result.dispatchReference ? ` — ${result.dispatchReference}` : ""}`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDispatch;
